/**
 * A module representing dialog.
 * @module dialog
 * @exports Dialog
 * @see Dialog
 * @example
 * var Dialog = require('dialog');
 */
define('dialog',['pubsub'], function(PubSub) {
    'use strict';

    /**
     * @constructor
     * @alias Dialog
     * @param {Object}  options
     * @param {Element} [options.dialog = '#js-dialog' container] - dialog container
     * @param {String}  [options.mainClass = 'dialog'] - Container CSS class

     * @param {Boolean} [options.clone = false] - Property to clone the container
     * @param {Integer} [options.closeIn = false] - Property to clone the container

     * @param {Boolean} [options.showClose = false] - Show close icon
     * @param {Boolean} [options.escapeClose = false] - Close dialog on escape key press
     * @param {Boolean} [options.enterClose = false] - Close dialog on enter key press
     * @param {String}  [options.label = ''] - Label content
     * @param {String}  [options.content = ''] - Main content
     * @param {String}  [options.footnote = ''] - Footer content
     */
    var Dialog = function(options)  {
        options = options || {};

        this.dialog = options.dialog || document.getElementById('js-dialog');
        this.mainClass = options.mainClass || 'dialog';

        this.clone = options.clone || false;
        this.closeIn = (options.closeIn) || 0;
        this.container = options.container || document.body;

        this.showClose = (options.showClose === false) ? false : true;
        this.escapeClose = options.escapeClose ? true : false;
        this.enterClose = options.enterClose ? true : false;

        this.label = options.label || '';
        this.content = options.content || '';
        this.footnote = options.footnote || '';

        this.type = options.type || '';

        this.buttons = Array.isArray(options.buttons) ? options.buttons : [];

        this.overlay = (options.overlay === false) ? false : true;

        if (!this.dialog) {
            return;
        }

        this.init();
    };

    /**
     * Append content to dialog section.
     * @param {Element} node - Element to append to
     * @param {} content - Content to append ()
     */
    Dialog.prototype.append = function(node, content) {

        if (typeof content === 'number' || typeof content === 'string') {
            node.innerHTML = content;
        } else if (content && typeof content === 'object' && content.nodeType) {
            node.appendChild(content);
        }
    };

    /**
     * Append CSS class to a dialog.
     * @param {String} className - CSS Class
     */
    Dialog.prototype.addClass = function(className) {
        if (!this.dialog) {
            return;
        }

        this.dialog.classList.add(className);
    };

    /**
     * Remove CSS class from a dialog.
     * @param {String} className - CSS Class
     */
    Dialog.prototype.removeClass = function(className) {
        if (!this.dialog) {
            return;
        }

        this.dialog.classList.remove(className);
    };

    /**
     * Check if dialog contains CSS class.
     * @param {String} className - CSS Class
     */
    Dialog.prototype.hasClass = function (className) {
        return (this.dialog && !!this.dialog.classList.contains(className)) ? true: false;
    },

        /**
         * Method to focus dialog.
         */
        Dialog.prototype.setFocus = function() {
            if (this.dialog) {
                this.dialog.focus();
            }
        };

    /**
     * Check if dialog is visible.
     */
    Dialog.prototype.isVisible = function() {
        return (this.dialog && !!this.dialog.classList.contains('visible')) ? true : false;
    };

    /**
     * Open a dialog.
     */
    Dialog.prototype.open = function() {
        if (!this.dialog || !this.content) {
            return;
        }

        this.addClass('visible');
        this.setFocus();
        PubSub.publish('dialog-open', {
            dialog: this
        });
    };

    /**
     * Close a dialog.
     */
    Dialog.prototype.close = function() {
        if (!this.dialog) {
            return;
        }

        this.dialog.className = this.mainClass;

        if (this.clone) {
            this.dialog.remove();
        }

        this.dialog = null;
    };

    /**
     * Set content to dialog label.
     * @param {} content - Content
     */
    Dialog.prototype.setLabel = function(content) {
        this.label = content;

        if (!this.dialog || !this.label) {
            return;
        }

        this.removeClass('no-label');

        this.append(this.header, this.label);
    };

    /**
     * Set main content to dialog.
     * @param {} content - Content
     */
    Dialog.prototype.setContent = function(content) {
        this.content = content;

        if (!this.dialog || !this.content) {
            return;
        }

        this.removeClass('no-content');

        this.append(this.body, this.content);
    };

    /**
     * Set content to dialog footer.
     * @param {} content - Content
     */
    Dialog.prototype.setFootnote = function(content) {
        this.footnote = content;

        if (!this.dialog || !this.footnote) {
            return;
        }

        this.removeClass('no-footnote');

        this.append(this.footer, this.footnote);
    };

    Dialog.prototype.addButtons = function(arr) {
        var btn,
            res = document.createElement('p');

        if (!this.dialog || !this.footer) {
            return;
        }

        for(var i = 0; i < arr.length; i++) {
            btn = document.createElement('button');
            btn.className = arr[i].class;
            btn.innerHTML = arr[i].text;

            if (arr[i].action) {
                btn.addEventListener('click', arr[i].action);
            }
            res.appendChild(btn);
        }

        while (this.footer.firstChild) {
            this.footer.removeChild(this.footer.firstChild);
        }

        this.setFootnote(res);
    };

    Dialog.prototype.init = function() {
        var dialog = this,
            headers, bodies, footers, dismiss;

        if (this.clone) {
            this.dialog = this.dialog.cloneNode(true);
            this.dialog.removeAttribute('id');
            this.container.appendChild(this.dialog);
        }

        if (!this.overlay) {
            this.addClass('no-overlay');
        }

        if (this.type !== 'feedback') {
            this.addClass('no-navigation');
        }

        if (this.mainClass) {
            var classes = this.mainClass.split(' ');

            for (var i = 0; i < classes.length; i++) {
                this.dialog.classList.add(classes[i]);
            }
        }

        headers = this.dialog.querySelectorAll('[data-header]');
        this.header = (headers && headers.length) ? headers[0] : null;

        if (this.label && this.header) {
            this.setLabel(this.label);
        } else if (this.header) {
            this.addClass('no-label');
        }

        bodies = this.dialog.querySelectorAll('[data-body]');
        this.body = (bodies && bodies.length) ? bodies[0] : null;

        if (this.content && this.body) {
            this.setContent(this.content);
        } else if (this.body) {
            this.addClass('no-content');
        }

        footers = this.dialog.querySelectorAll('[data-footer]');
        this.footer = (footers && footers.length) ? footers[0] : null;

        if (this.footnote && this.footer) {
            this.setFootnote(this.footnote);
        } else if (this.footer) {
            this.addClass('no-footnote');
        }

        dismiss = this.dialog.querySelectorAll('[data-dismiss]');
        this.dismiss = (dismiss && dismiss.length) ? dismiss[0] : null;

        if (this.showClose && this.dismiss) {
            this.dismiss.addEventListener('click', function(e) {
                e.preventDefault();
                dialog.close();
            });

        } else if (this.dismiss) {
            this.addClass('no-close');
        }

        if (this.buttons.length) {
            this.addButtons(this.buttons);
        }

        if (this.closeIn) {
            setTimeout(function() {
                dialog.close();
            }, this.closeIn);
        }

        document.body.addEventListener('keydown', function(e) {
            e = e || window.event;

            if (dialog.isVisible()) {
                if (e.keyCode === 13 && dialog.enterClose) { // close dialog on ENTER
                    dialog.close();
                    e.stopPropagation();
                } else if (e.keyCode === 27 && dialog.showClose && dialog.dismiss && dialog.escapeClose) { // close dialog on ESC
                    dialog.close();
                    e.stopPropagation();
                } else if (e.keyCode === 37 || e.keyCode === 39) {
                    if (dialog.hasClass('no-navigation')) {
                        e.stopPropagation();
                    }
                }
            }

        });
    };

    return Dialog;

});

